/**
 * Number formatting composable that can be initialized with a specific locale
 * @param {string} [initialLocale] - The locale to use for formatting (defaults to 'en-us')
 * @returns {Object} Object containing formatting functions
 */
export function useNumberFormat(initialLocale = "en-us") {
    // Convert Vue locale to Intl locale format
    const intlLocale = initialLocale === "fr" ? "fr-CA" : "en-us";

    function dec2(value) {
        return new Intl.NumberFormat(intlLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    function comma(value) {
        return new Intl.NumberFormat(intlLocale).format(value);
    }

    function formatNumberWithSuffix(value) {
        const formatter = new Intl.NumberFormat(intlLocale, {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formatter.format(value);
    }

    function formatNumber(value) {
        if (value === 0) {
            return "0";
        }
        const formatter = new Intl.NumberFormat(intlLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formatter.format(value);
    }

    function smartDecimal(value) {
        if (value === 0) {
            return "0";
        }

        const absValue = Math.abs(value);
        const sign = value < 0 ? "-" : "";

        if (absValue < 0.0001) {
            return sign + absValue.toFixed(8).replace(/(\.\d*?[1-9])0+$/, "$1");
        }

        // For regular numbers, use different formatting approaches
        if (absValue >= 1) {
            // For numbers >= 1, always display at least 2 decimal places
            const compactFormatter = new Intl.NumberFormat(intlLocale, {
                notation: "compact",
                compactDisplay: "short",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return compactFormatter.format(value);
        } else {
            // For numbers between 0.0001 and 1, allow up to 4 decimals without trailing zeros
            // but keep at least 2 decimal places
            const maxDecimals = 4;

            // Convert to string with fixed decimals then remove trailing zeros
            // but keep at least 2 decimal places
            let numStr = absValue.toFixed(maxDecimals);
            let decimalPart = numStr.split(".")[1] || "";

            // If decimal part has fewer than 2 digits after removing trailing zeros,
            // ensure we keep at least 2
            if (decimalPart.replace(/0+$/, "").length < 2) {
                numStr = absValue.toFixed(2);
            } else {
                numStr = numStr.replace(/(\.\d{2,}?)0+$/, "$1");
            }

            const compactFormatter2 = new Intl.NumberFormat(intlLocale, {
                notation: "compact",
                compactDisplay: "short",
            });

            return compactFormatter2.format(value < 0 ? -Number(numStr) : Number(numStr));
        }
    }

    /**
     * Format financial prices (no suffixes, precise decimals)
     * For stock prices, changes, etc. where you want exact values
     */
    function formatPrice(value, locale = null) {
        // If value is null, undefined, or NaN, return a placeholder
        if (value === null || value === undefined || isNaN(value)) {
            return "—";
        }

        const formatLocale = locale || intlLocale;
        const absValue = Math.abs(value);

        if (value === 0) {
            return "0";
        }

        // For very small numbers (like 0.00043565), find the first two significant digits
        if (absValue < 0.01) {
            const str = absValue.toString();
            const scientificMatch = str.match(/^(\d+\.?\d*)e-(\d+)$/);

            if (scientificMatch) {
                // Handle scientific notation
                const [, coefficient, exponent] = scientificMatch;
                const decimalPlaces = parseInt(exponent) + 1;
                const formatted = new Intl.NumberFormat(formatLocale, {
                    minimumFractionDigits: decimalPlaces,
                    maximumFractionDigits: decimalPlaces,
                }).format(value);

                // Check if second significant digit is 0 and remove it
                const formattedStr = Math.abs(value).toFixed(decimalPlaces);
                const decimalPart = formattedStr.split(".")[1];
                const firstNonZeroPos = decimalPart.search(/[1-9]/);
                if (
                    firstNonZeroPos >= 0 &&
                    firstNonZeroPos + 1 < decimalPart.length &&
                    decimalPart[firstNonZeroPos + 1] === "0"
                ) {
                    return new Intl.NumberFormat(formatLocale, {
                        minimumFractionDigits: decimalPlaces - 1,
                        maximumFractionDigits: decimalPlaces - 1,
                    }).format(value);
                }
                return formatted;
            } else {
                // Find position of first non-zero digit after decimal
                const decimalPart = str.split(".")[1] || "";
                let firstNonZeroIndex = -1;
                for (let i = 0; i < decimalPart.length; i++) {
                    if (decimalPart[i] !== "0") {
                        firstNonZeroIndex = i;
                        break;
                    }
                }

                if (firstNonZeroIndex >= 0) {
                    // Check if second significant digit is 0
                    const secondDigitIndex = firstNonZeroIndex + 1;
                    const hasSecondDigit = secondDigitIndex < decimalPart.length;
                    const secondDigitIsZero =
                        hasSecondDigit && decimalPart[secondDigitIndex] === "0";

                    // Show 1 significant digit if second is 0, otherwise show 2
                    const decimalPlaces = secondDigitIsZero
                        ? firstNonZeroIndex + 1
                        : firstNonZeroIndex + 2;
                    return new Intl.NumberFormat(formatLocale, {
                        minimumFractionDigits: decimalPlaces,
                        maximumFractionDigits: decimalPlaces,
                    }).format(value);
                }
            }
        }

        // For regular prices
        if (absValue >= 1) {
            return new Intl.NumberFormat(formatLocale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(value);
        } else {
            return new Intl.NumberFormat(formatLocale, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 4,
            }).format(value);
        }
    }

    /**
     * Format percentage changes (with % sign, 2 decimal places)
     */
    function formatPercent(value, locale = null) {
        if (value === null || value === undefined || isNaN(value)) {
            return "—";
        }

        const formatLocale = locale || intlLocale;
        return new Intl.NumberFormat(formatLocale, {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value / 100);
    }

    /**
     * Format large numbers with suffixes (for market cap, volume, etc.)
     */
    function formatLargeNumber(value, locale = null) {
        if (value === null || value === undefined || isNaN(value)) {
            return "—";
        }

        if (value === 0) {
            return "0";
        }

        const absValue = Math.abs(value);

        if (absValue < 100000) {
            return formatNumber(value);
        }

        const formatLocale = locale || intlLocale;
        return new Intl.NumberFormat(formatLocale, {
            notation: "compact",
            compactDisplay: "short",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    return {
        dec2,
        comma,
        formatNumberWithSuffix,
        formatNumber,
        smartDecimal,
        formatPrice,
        formatPercent,
        formatLargeNumber,
    };
}
